import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { ApiError, type components } from '@akillidernek/api-client'
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Dialog,
  EmptyState,
  Field,
  Input,
  useToast,
} from '@akillidernek/ui'
import { api } from '../api'
import { useTenantSlug } from '../use-tenant'

type Kurul = components['schemas']['BoardRead']
type KurulUyesi = components['schemas']['BoardMemberRead']
type Toplanti = components['schemas']['MeetingRead']
type Karar = components['schemas']['DecisionRead']

const KURUL_TURU: Record<Kurul['kind'], string> = {
  yonetim: 'Yönetim Kurulu',
  denetim: 'Denetim Kurulu',
  disiplin: 'Disiplin Kurulu',
  diger: 'Diğer',
}

const GOREV: Record<KurulUyesi['role'], string> = {
  baskan: 'Başkan',
  baskan_yardimcisi: 'Başkan Yardımcısı',
  genel_sekreter: 'Genel Sekreter',
  sayman: 'Sayman',
  uye: 'Üye',
  yedek: 'Yedek Üye',
}

const SONUC: Record<Karar['outcome'], { etiket: string; ton: 'success' | 'danger' | 'neutral' }> = {
  accepted: { etiket: 'Kabul', ton: 'success' },
  rejected: { etiket: 'Ret', ton: 'danger' },
  deferred: { etiket: 'Ertelendi', ton: 'neutral' },
}

const bugun = () => new Date().toISOString().slice(0, 10)
const tarih = (iso: string) => new Date(iso).toLocaleDateString('tr-TR')

const selectSinifi =
  'h-9 w-full rounded-[var(--radius-control)] border border-[var(--ad-border)] bg-[var(--ad-surface)] px-2 text-sm'

function hataMesaji(error: unknown) {
  return error instanceof ApiError ? error.problem.detail : 'Beklenmeyen bir hata oluştu.'
}

/**
 * Kurullar ve karar defteri — YES-163.
 *
 * Üç katman: kurul → toplantı → karar. Karar numarası dernek genelinde
 * aralıksızdır; bu yüzden "Sil" yok — yanlış karar yeni bir kararla düzeltilir,
 * defterde de öyle yapılır.
 */
export function Kurullar() {
  const slug = useTenantSlug()
  const [sekme, setSekme] = useState<'kurullar' | 'defter'>('kurullar')
  const [seciliKurul, setSeciliKurul] = useState<string | null>(null)

  const kurullar = useQuery({
    queryKey: ['boards', slug],
    enabled: Boolean(slug),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/governance/boards')
      return data ?? []
    },
  })

  const yetkisiz = kurullar.error instanceof ApiError && kurullar.error.problem.status === 403
  const kurul = (kurullar.data ?? []).find((k) => k.id === seciliKurul) ?? kurullar.data?.[0]

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold">Kurullar</h1>
        <div className="flex gap-1" role="tablist">
          <Button
            size="sm"
            variant={sekme === 'kurullar' ? 'primary' : 'ghost'}
            onClick={() => setSekme('kurullar')}
          >
            Kurullar
          </Button>
          <Button
            size="sm"
            variant={sekme === 'defter' ? 'primary' : 'ghost'}
            onClick={() => setSekme('defter')}
          >
            Karar defteri
          </Button>
        </div>
      </div>

      {yetkisiz ? (
        <Card>
          <CardBody className="text-sm">Kurulları görme yetkiniz yok.</CardBody>
        </Card>
      ) : sekme === 'defter' ? (
        <KararDefteri />
      ) : (
        <div className="grid gap-4 lg:grid-cols-[18rem_1fr]">
          <KurulListesi
            kurullar={kurullar.data ?? []}
            secili={kurul?.id ?? null}
            onSec={setSeciliKurul}
          />
          {kurul ? (
            <KurulDetay kurul={kurul} />
          ) : (
            <Card>
              <CardBody>
                <EmptyState
                  title="Henüz kurul yok"
                  description="Genel kurul seçimiyle oluşan yönetim ve denetim kurulunu buradan açın."
                />
              </CardBody>
            </Card>
          )}
        </div>
      )}
    </div>
  )
}

function KurulListesi({
  kurullar,
  secili,
  onSec,
}: {
  kurullar: Kurul[]
  secili: string | null
  onSec: (id: string) => void
}) {
  const slug = useTenantSlug()
  const toast = useToast()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [hatalar, setHatalar] = useState<Record<string, string>>({})

  const ac = useMutation({
    mutationFn: async (form: FormData) => {
      const bitis = String(form.get('term_end') ?? '')
      const { data } = await api.POST('/api/v1/governance/boards', {
        body: {
          kind: String(form.get('kind')) as Kurul['kind'],
          name: String(form.get('name') ?? '').trim(),
          term_start: String(form.get('term_start')),
          term_end: bitis || null,
        },
      })
      return data
    },
    onSuccess: (data) => {
      setOpen(false)
      setHatalar({})
      toast({ title: 'Kurul açıldı', tone: 'success' })
      void queryClient.invalidateQueries({ queryKey: ['boards', slug] })
      if (data) onSec(data.id)
    },
    onError: (error) => {
      if (error instanceof ApiError) setHatalar(error.fieldErrors())
      toast({ title: 'Kurul açılamadı', description: hataMesaji(error), tone: 'error' })
    },
  })

  return (
    <Card>
      <CardHeader
        title="Kurullar"
        action={
          <Button size="sm" onClick={() => setOpen(true)}>
            Kurul aç
          </Button>
        }
      />
      <CardBody className="flex flex-col gap-1">
        {kurullar.length === 0 && (
          <p className="text-sm text-[var(--ad-text-muted)]">Henüz kurul yok.</p>
        )}
        {kurullar.map((k) => (
          <button
            key={k.id}
            type="button"
            onClick={() => onSec(k.id)}
            aria-current={k.id === secili ? 'true' : undefined}
            className={`flex flex-col items-start rounded-[var(--radius-control)] px-3 py-2 text-left text-sm hover:bg-[var(--ad-surface-muted)] ${
              k.id === secili ? 'bg-[var(--ad-surface-muted)]' : ''
            }`}
          >
            <span className="flex items-center gap-2 font-medium">
              {k.name}
              {!k.is_active && <Badge tone="neutral">Kapalı</Badge>}
            </span>
            <span className="text-xs text-[var(--ad-text-muted)]">
              {KURUL_TURU[k.kind]} · {tarih(k.term_start)}
              {k.term_end ? ` – ${tarih(k.term_end)}` : ' – sürüyor'}
            </span>
          </button>
        ))}
      </CardBody>

      <Dialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next)
          if (!next) setHatalar({})
        }}
        title="Kurul aç"
        description="Genel kurulun seçtiği organ; dönem başlangıcı seçim tarihidir."
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Vazgeç
            </Button>
            <Button type="submit" form="kurul-formu" disabled={ac.isPending}>
              {ac.isPending ? 'Açılıyor…' : 'Aç'}
            </Button>
          </>
        }
      >
        <form
          id="kurul-formu"
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault()
            ac.mutate(new FormData(e.currentTarget))
          }}
        >
          <Field label="Tür" required>
            {({ id }) => (
              <select id={id} name="kind" defaultValue="yonetim" className={selectSinifi}>
                {Object.entries(KURUL_TURU).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            )}
          </Field>
          <Field label="Ad" required error={hatalar.name}>
            {(props) => <Input name="name" placeholder="2026-2029 Yönetim Kurulu" {...props} />}
          </Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Dönem başlangıcı" required error={hatalar.term_start}>
              {(props) => <Input name="term_start" type="date" defaultValue={bugun()} {...props} />}
            </Field>
            <Field label="Dönem bitişi" hint="Boş: sürüyor." error={hatalar.term_end}>
              {(props) => <Input name="term_end" type="date" {...props} />}
            </Field>
          </div>
        </form>
      </Dialog>
    </Card>
  )
}

function KurulDetay({ kurul }: { kurul: Kurul }) {
  const slug = useTenantSlug()
  const toast = useToast()
  const queryClient = useQueryClient()
  const [seciliToplanti, setSeciliToplanti] = useState<string | null>(null)

  const uyeler = useQuery({
    queryKey: ['board-members', slug, kurul.id],
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/governance/boards/{board_id}/members', {
        params: { path: { board_id: kurul.id } },
      })
      return data ?? []
    },
  })
  const toplantilar = useQuery({
    queryKey: ['meetings', slug, kurul.id],
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/governance/meetings', {
        params: { query: { board_id: kurul.id } },
      })
      return data ?? []
    },
  })

  const kapat = useMutation({
    mutationFn: async () => {
      await api.PATCH('/api/v1/governance/boards/{board_id}', {
        params: { path: { board_id: kurul.id } },
        body: { is_active: false, term_end: bugun() },
      })
    },
    onSuccess: () => {
      toast({ title: 'Kurul kapatıldı', tone: 'success' })
      void queryClient.invalidateQueries({ queryKey: ['boards', slug] })
    },
    onError: (error) =>
      toast({ title: 'Kapatılamadı', description: hataMesaji(error), tone: 'error' }),
  })

  const toplanti =
    (toplantilar.data ?? []).find((t) => t.id === seciliToplanti) ?? toplantilar.data?.[0]

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader
          title={kurul.name}
          description={`${KURUL_TURU[kurul.kind]} · dönem ${tarih(kurul.term_start)}${
            kurul.term_end ? ` – ${tarih(kurul.term_end)}` : ' – sürüyor'
          }`}
          action={
            kurul.is_active ? (
              <Button
                size="sm"
                variant="secondary"
                disabled={kapat.isPending}
                onClick={() => {
                  if (window.confirm(`${kurul.name} kapatılsın mı? Yeni toplantı açılamaz.`))
                    kapat.mutate()
                }}
              >
                Kurulu kapat
              </Button>
            ) : (
              <Badge tone="neutral">Kapalı</Badge>
            )
          }
        />
        <CardBody>
          <KurulUyeleri kurul={kurul} uyeler={uyeler.data ?? []} />
        </CardBody>
      </Card>

      <div className="grid gap-4 lg:grid-cols-[16rem_1fr]">
        <ToplantiListesi
          kurul={kurul}
          uyeler={uyeler.data ?? []}
          toplantilar={toplantilar.data ?? []}
          secili={toplanti?.id ?? null}
          onSec={setSeciliToplanti}
        />
        {toplanti ? (
          <ToplantiDetay toplanti={toplanti} uyeler={uyeler.data ?? []} />
        ) : (
          <Card>
            <CardBody>
              <EmptyState
                title="Henüz toplantı yok"
                description="İlk toplantıyı açın; kararlar toplantının altına yazılır."
              />
            </CardBody>
          </Card>
        )}
      </div>
    </div>
  )
}

function KurulUyeleri({ kurul, uyeler }: { kurul: Kurul; uyeler: KurulUyesi[] }) {
  const slug = useTenantSlug()
  const toast = useToast()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [hatalar, setHatalar] = useState<Record<string, string>>({})

  const adaylar = useQuery({
    queryKey: ['members', slug, 'aktif-secim'],
    enabled: open,
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/members', {
        params: { query: { membership_status: 'active', limit: 200 } },
      })
      return data?.items ?? []
    },
  })

  const yenile = () =>
    queryClient.invalidateQueries({ queryKey: ['board-members', slug, kurul.id] })

  const ekle = useMutation({
    mutationFn: async (form: FormData) => {
      await api.POST('/api/v1/governance/boards/{board_id}/members', {
        params: { path: { board_id: kurul.id } },
        body: {
          membership_id: String(form.get('membership_id')),
          role: String(form.get('role')) as KurulUyesi['role'],
          started_on: String(form.get('started_on')),
        },
      })
    },
    onSuccess: () => {
      setOpen(false)
      setHatalar({})
      toast({ title: 'Kurula eklendi', tone: 'success' })
      void yenile()
    },
    onError: (error) => {
      if (error instanceof ApiError) setHatalar(error.fieldErrors())
      toast({ title: 'Eklenemedi', description: hataMesaji(error), tone: 'error' })
    },
  })

  const bitir = useMutation({
    mutationFn: async (uye: KurulUyesi) => {
      await api.POST('/api/v1/governance/boards/{board_id}/members/{member_id}/end', {
        params: { path: { board_id: kurul.id, member_id: uye.id } },
        body: { ended_on: bugun() },
      })
    },
    onSuccess: () => {
      toast({ title: 'Görev sonlandırıldı', tone: 'success' })
      void yenile()
    },
    onError: (error) =>
      toast({ title: 'Sonlandırılamadı', description: hataMesaji(error), tone: 'error' }),
  })

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-medium">Kurul üyeleri</h2>
        {kurul.is_active && (
          <Button size="sm" variant="secondary" onClick={() => setOpen(true)}>
            Üye ekle
          </Button>
        )}
      </div>
      {uyeler.length === 0 ? (
        <p className="text-sm text-[var(--ad-text-muted)]">Henüz üye atanmadı.</p>
      ) : (
        <ul className="divide-y divide-[var(--ad-border)] text-sm">
          {uyeler.map((u) => (
            <li key={u.id} className="flex items-center gap-3 py-2">
              <span className="min-w-0 flex-1">
                <span className={u.ended_on ? 'text-[var(--ad-text-muted)] line-through' : ''}>
                  {u.member_name}
                </span>
                {u.member_no && (
                  <span className="ml-2 font-mono text-xs text-[var(--ad-text-muted)]">
                    #{u.member_no}
                  </span>
                )}
              </span>
              <Badge tone={u.role === 'yedek' ? 'neutral' : 'info'}>{GOREV[u.role]}</Badge>
              <span className="w-40 text-xs text-[var(--ad-text-muted)]">
                {tarih(u.started_on)}
                {u.ended_on ? ` – ${tarih(u.ended_on)}` : ''}
              </span>
              {!u.ended_on && kurul.is_active && (
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={bitir.isPending}
                  onClick={() => bitir.mutate(u)}
                >
                  Görevi bitir
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      <Dialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next)
          if (!next) setHatalar({})
        }}
        title="Kurula üye ekle"
        description="Yalnızca aktif dernek üyeleri kurulda görev alabilir."
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Vazgeç
            </Button>
            <Button type="submit" form="kurul-uye-formu" disabled={ekle.isPending}>
              {ekle.isPending ? 'Ekleniyor…' : 'Ekle'}
            </Button>
          </>
        }
      >
        <form
          id="kurul-uye-formu"
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault()
            ekle.mutate(new FormData(e.currentTarget))
          }}
        >
          <Field label="Üye" required error={hatalar.membership_id}>
            {({ id }) => (
              <select id={id} name="membership_id" required className={selectSinifi}>
                <option value="">Seçin…</option>
                {(adaylar.data ?? []).map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.first_name} {m.last_name}
                    {m.member_no ? ` (#${m.member_no})` : ''}
                  </option>
                ))}
              </select>
            )}
          </Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Görev" required>
              {({ id }) => (
                <select id={id} name="role" defaultValue="uye" className={selectSinifi}>
                  {Object.entries(GOREV).map(([k, v]) => (
                    <option key={k} value={k}>
                      {v}
                    </option>
                  ))}
                </select>
              )}
            </Field>
            <Field label="Görev başlangıcı" required error={hatalar.started_on}>
              {(props) => (
                <Input name="started_on" type="date" defaultValue={kurul.term_start} {...props} />
              )}
            </Field>
          </div>
        </form>
      </Dialog>
    </div>
  )
}

function ToplantiListesi({
  kurul,
  uyeler,
  toplantilar,
  secili,
  onSec,
}: {
  kurul: Kurul
  uyeler: KurulUyesi[]
  toplantilar: Toplanti[]
  secili: string | null
  onSec: (id: string) => void
}) {
  const slug = useTenantSlug()
  const toast = useToast()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [hatalar, setHatalar] = useState<Record<string, string>>({})
  const gorevdekiler = uyeler.filter((u) => !u.ended_on)

  const ac = useMutation({
    mutationFn: async (form: FormData) => {
      const { data } = await api.POST('/api/v1/governance/meetings', {
        body: {
          board_id: kurul.id,
          held_on: String(form.get('held_on')),
          location: String(form.get('location') ?? '').trim() || null,
          agenda: String(form.get('agenda') ?? '').trim() || null,
          attendee_ids: form.getAll('attendee_ids').map(String),
        },
      })
      return data
    },
    onSuccess: (data) => {
      setOpen(false)
      setHatalar({})
      toast({ title: 'Toplantı açıldı', tone: 'success' })
      void queryClient.invalidateQueries({ queryKey: ['meetings', slug, kurul.id] })
      if (data) onSec(data.id)
    },
    onError: (error) => {
      if (error instanceof ApiError) setHatalar(error.fieldErrors())
      toast({ title: 'Toplantı açılamadı', description: hataMesaji(error), tone: 'error' })
    },
  })

  return (
    <Card>
      <CardHeader
        title="Toplantılar"
        action={
          kurul.is_active ? (
            <Button size="sm" onClick={() => setOpen(true)}>
              Toplantı aç
            </Button>
          ) : undefined
        }
      />
      <CardBody className="flex flex-col gap-1">
        {toplantilar.length === 0 && (
          <p className="text-sm text-[var(--ad-text-muted)]">Henüz toplantı yok.</p>
        )}
        {toplantilar.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => onSec(t.id)}
            aria-current={t.id === secili ? 'true' : undefined}
            className={`flex items-center justify-between rounded-[var(--radius-control)] px-3 py-2 text-left text-sm hover:bg-[var(--ad-surface-muted)] ${
              t.id === secili ? 'bg-[var(--ad-surface-muted)]' : ''
            }`}
          >
            <span className="font-medium">{t.number}. toplantı</span>
            <span className="text-xs text-[var(--ad-text-muted)]">{tarih(t.held_on)}</span>
          </button>
        ))}
      </CardBody>

      <Dialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next)
          if (!next) setHatalar({})
        }}
        title="Toplantı aç"
        description="Sıra numarası kurul içinde kendiliğinden verilir."
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Vazgeç
            </Button>
            <Button type="submit" form="toplanti-formu" disabled={ac.isPending}>
              {ac.isPending ? 'Açılıyor…' : 'Aç'}
            </Button>
          </>
        }
      >
        <form
          id="toplanti-formu"
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault()
            ac.mutate(new FormData(e.currentTarget))
          }}
        >
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Tarih" required error={hatalar.held_on}>
              {(props) => <Input name="held_on" type="date" defaultValue={bugun()} {...props} />}
            </Field>
            <Field label="Yer" error={hatalar.location}>
              {(props) => <Input name="location" placeholder="Dernek merkezi" {...props} />}
            </Field>
          </div>
          <Field label="Gündem" error={hatalar.agenda}>
            {(props) => (
              <Input name="agenda" placeholder="1. Aidat artışı 2. Üye başvuruları" {...props} />
            )}
          </Field>
          <fieldset className="flex flex-col gap-1">
            <legend className="pb-1 text-xs font-medium uppercase tracking-wide text-[var(--ad-text-muted)]">
              Katılanlar
            </legend>
            {gorevdekiler.length === 0 && (
              <p className="text-xs text-[var(--ad-text-muted)]">Kurulda görevli üye yok.</p>
            )}
            {gorevdekiler.map((u) => (
              <label key={u.id} className="flex items-center gap-2 text-sm">
                <input type="checkbox" name="attendee_ids" value={u.id} defaultChecked />
                {u.member_name}
                <span className="text-xs text-[var(--ad-text-muted)]">{GOREV[u.role]}</span>
              </label>
            ))}
            {hatalar.attendee_ids && (
              <p className="text-xs text-[var(--ad-danger)]">{hatalar.attendee_ids}</p>
            )}
          </fieldset>
        </form>
      </Dialog>
    </Card>
  )
}

function ToplantiDetay({ toplanti, uyeler }: { toplanti: Toplanti; uyeler: KurulUyesi[] }) {
  const slug = useTenantSlug()
  const toast = useToast()
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [hatalar, setHatalar] = useState<Record<string, string>>({})

  const detay = useQuery({
    queryKey: ['meeting', slug, toplanti.id],
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/governance/meetings/{meeting_id}', {
        params: { path: { meeting_id: toplanti.id } },
      })
      return data
    },
  })

  const yaz = useMutation({
    mutationFn: async (form: FormData) => {
      await api.POST('/api/v1/governance/meetings/{meeting_id}/decisions', {
        params: { path: { meeting_id: toplanti.id } },
        body: {
          subject: String(form.get('subject') ?? '').trim(),
          body: String(form.get('body') ?? '').trim(),
          outcome: String(form.get('outcome')) as Karar['outcome'],
        },
      })
    },
    onSuccess: () => {
      setOpen(false)
      setHatalar({})
      toast({ title: 'Karar deftere yazıldı', tone: 'success' })
      void queryClient.invalidateQueries({ queryKey: ['meeting', slug, toplanti.id] })
      void queryClient.invalidateQueries({ queryKey: ['decisions', slug] })
    },
    onError: (error) => {
      if (error instanceof ApiError) setHatalar(error.fieldErrors())
      toast({ title: 'Yazılamadı', description: hataMesaji(error), tone: 'error' })
    },
  })

  const katilanlar = uyeler.filter((u) => toplanti.attendee_ids.includes(u.id))

  return (
    <Card>
      <CardHeader
        title={`${toplanti.number}. toplantı · ${tarih(toplanti.held_on)}`}
        description={[toplanti.location, toplanti.agenda].filter(Boolean).join(' · ') || undefined}
        action={
          <Button size="sm" onClick={() => setOpen(true)}>
            Karar yaz
          </Button>
        }
      />
      <CardBody className="flex flex-col gap-4">
        <p className="text-xs text-[var(--ad-text-muted)]">
          Katılanlar:{' '}
          {katilanlar.length ? katilanlar.map((k) => k.member_name).join(', ') : 'kayıt yok'}
        </p>
        {detay.data && detay.data.decisions.length === 0 ? (
          <p className="text-sm text-[var(--ad-text-muted)]">Bu toplantıda henüz karar yok.</p>
        ) : (
          <ol className="flex flex-col divide-y divide-[var(--ad-border)]">
            {(detay.data?.decisions ?? []).map((k) => (
              <KararSatiri key={k.id} karar={k} />
            ))}
          </ol>
        )}
      </CardBody>

      <Dialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next)
          if (!next) setHatalar({})
        }}
        title="Karar yaz"
        description="Numara kaydedildiği anda sırayla verilir ve geri alınmaz."
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Vazgeç
            </Button>
            <Button type="submit" form="karar-formu" disabled={yaz.isPending}>
              {yaz.isPending ? 'Yazılıyor…' : 'Deftere yaz'}
            </Button>
          </>
        }
      >
        <form
          id="karar-formu"
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault()
            yaz.mutate(new FormData(e.currentTarget))
          }}
        >
          <Field label="Konu" required error={hatalar.subject}>
            {(props) => (
              <Input name="subject" placeholder="Aidat tutarının güncellenmesi" {...props} />
            )}
          </Field>
          <Field label="Karar metni" required error={hatalar.body}>
            {({ id, invalid }) => (
              <textarea
                id={id}
                name="body"
                required
                rows={5}
                aria-invalid={invalid || undefined}
                className="w-full rounded-[var(--radius-control)] border border-[var(--ad-border)] bg-[var(--ad-surface)] p-2 text-sm"
                placeholder="Yıllık aidatın 1 Ocak'tan itibaren 600 TL olarak uygulanmasına oy birliğiyle karar verildi."
              />
            )}
          </Field>
          <Field label="Sonuç" required>
            {({ id }) => (
              <select id={id} name="outcome" defaultValue="accepted" className={selectSinifi}>
                {Object.entries(SONUC).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v.etiket}
                  </option>
                ))}
              </select>
            )}
          </Field>
        </form>
      </Dialog>
    </Card>
  )
}

function KararSatiri({ karar, toplantiEtiketi }: { karar: Karar; toplantiEtiketi?: string }) {
  const sonuc = SONUC[karar.outcome]
  return (
    <li className="flex items-start gap-3 py-3 text-sm">
      <span className="w-14 shrink-0 font-mono text-xs text-[var(--ad-text-muted)]">
        No {karar.number}
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-2 font-medium">
          {karar.subject}
          <Badge tone={sonuc.ton}>{sonuc.etiket}</Badge>
        </span>
        <span className="block whitespace-pre-wrap text-xs text-[var(--ad-text-muted)]">
          {karar.body}
        </span>
        {toplantiEtiketi && (
          <span className="block text-xs text-[var(--ad-text-muted)]">{toplantiEtiketi}</span>
        )}
      </span>
    </li>
  )
}

/** Bütün kurulların kararları, numaraya göre; konuda arama. */
function KararDefteri() {
  const slug = useTenantSlug()
  const [arama, setArama] = useState('')

  const kararlar = useQuery({
    queryKey: ['decisions', slug, arama],
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/governance/decisions', {
        params: { query: { q: arama || undefined, limit: 500 } },
      })
      return data ?? []
    },
  })
  const toplantilar = useQuery({
    queryKey: ['meetings', slug, 'hepsi'],
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/governance/meetings', {
        params: { query: { limit: 500 } },
      })
      return data ?? []
    },
  })
  const toplantiAdi = new Map(
    (toplantilar.data ?? []).map((t) => [t.id, `${t.number}. toplantı · ${tarih(t.held_on)}`]),
  )

  return (
    <Card>
      <CardHeader
        title="Karar defteri"
        description="Numara dernek genelinde aralıksızdır; kararlar silinmez, yeni kararla düzeltilir."
        action={
          <Input
            aria-label="Kararlarda ara"
            placeholder="Konuda ara…"
            value={arama}
            onChange={(e) => setArama(e.target.value)}
          />
        }
      />
      <CardBody>
        {kararlar.data && kararlar.data.length === 0 ? (
          <EmptyState
            title="Defter boş"
            description={
              arama ? 'Bu aramayla eşleşen karar yok.' : 'İlk kararı bir toplantının altına yazın.'
            }
          />
        ) : (
          <ol className="flex flex-col divide-y divide-[var(--ad-border)]">
            {(kararlar.data ?? []).map((k) => (
              <KararSatiri key={k.id} karar={k} toplantiEtiketi={toplantiAdi.get(k.meeting_id)} />
            ))}
          </ol>
        )}
      </CardBody>
    </Card>
  )
}
